import { Box, Heading, HStack } from "@chakra-ui/layout";
import {
  Flex,
  Icon,
  IconButton,
  Link,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tag,
  Text,
} from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";
import { RiLightbulbLine, RiQuestionLine } from "react-icons/ri";
import { useLocation, useSearchParams } from "react-router-dom";
import { GenericError } from "src/components/Feedback/GenericError";
import { Loading } from "src/components/Feedback/Loading";
import { Breadcrumb } from "src/components/Navigation/Breadcrumb";
import { useFormTemplates } from "src/components/Providers/FormTemplateProvider";
import { SearchAndFilterTypes } from "src/constants";
import { useOrganization } from "src/hooks/useOrganization";
import * as breadcrumb from "src/services/breadcrumb";
import * as Url from "src/services/url";
import {
  ScheduleResponse,
  SelectionObject,
} from "../../dataServices/exports/types";
import { BulkExportForm } from "./BulkExportForm";
import { useFormTemplatePropsForExport } from "./useFormTemplatePropsForExport";
import { useScheduledExportQuery } from "./useScheduledExportQuery";

export type BulkExportFormProps = BulkExportState &
  FormTemplatePropsForExport & {
    formTemplateId: uuid;
    scheduledExport?: ScheduleResponse;
  };

export interface FormTemplatePropsForExport {
  questionIdsAndLabelsInOrder: {
    id: uuid;
    label: string;
    isArchived: boolean;
  }[];
  formVerifications?: { id: uuid; label: string }[];
  tagGroups?: { id: uuid; label: string }[];
  formTemplateConfig: {
    isLotteryEnabled: boolean;
    isRankingEnabled: boolean;
    disclaimerTitle?: string;
  };
}

export interface BulkExportState {
  selection: SelectionObject;
  dropoffFormTemplateId?: uuid;
}

export function BulkExport() {
  const organization = useOrganization();
  const { selectedNavFormTemplate, navFormTemplates } = useFormTemplates();
  const bulkExportProps = useFormTemplatePropsForExport(
    selectedNavFormTemplate?.id
  );

  const location = useLocation();
  const {
    selection: selectionFromLocalState,
    dropoffFormTemplateId: dropoffFormTemplateIdFromLocalState,
  } = (location.state ?? {}) as Partial<BulkExportState>;
  const [selection, setSelection] = useState<SelectionObject | undefined>(
    selectionFromLocalState
  );
  const [dropoffFormTemplateId, setDropoffFormTemplateId] = useState<
    uuid | undefined
  >(dropoffFormTemplateIdFromLocalState);

  const [searchParams] = useSearchParams();
  const scheduledExportId =
    searchParams.get(SearchAndFilterTypes.ScheduledExportId) ?? undefined;
  const {
    data: scheduledExportData,
    isLoading: isScheduleLoading,
    error: scheduleError,
  } = useScheduledExportQuery({
    orgId: organization.toNullable()?.id,
    scheduledExportId,
  });
  useEffect(() => {
    if (scheduledExportData) {
      setSelection(scheduledExportData.exportConfig.selection);

      const scheduledExportSearchParams =
        scheduledExportData.exportConfig.searchParamsString;
      const params = new URLSearchParams(scheduledExportSearchParams);
      const dropoffFormTemplateId =
        params.get(SearchAndFilterTypes.DropoffFormTemplateId) ?? undefined;
      setDropoffFormTemplateId(dropoffFormTemplateId);
    }
  }, [selectionFromLocalState, scheduledExportData]);

  const breadcrumbItems: breadcrumb.BreadcrumbType[] = useMemo(
    () => [
      ...breadcrumb.form.getBreadcrumbsForList(
        organization,
        selectedNavFormTemplate,
        scheduledExportData?.exportConfig.searchParamsString
      ),
      {
        label: scheduledExportId ? "Edit scheduled export" : "Export forms",
        href: "",
      },
    ],
    [
      organization,
      selectedNavFormTemplate,
      scheduledExportData,
      scheduledExportId,
    ]
  );

  const isLoading =
    navFormTemplates.isLoading() ||
    organization.isLoading() ||
    bulkExportProps.loading ||
    isScheduleLoading;

  const hasError =
    navFormTemplates.hasError() ||
    organization.hasError() ||
    bulkExportProps.error ||
    scheduleError;

  return (
    <Flex direction="column" gap="0.5rem" minBlockSize="100%">
      <Breadcrumb items={breadcrumbItems} mb={4} />
      <HStack pb={8}>
        <Heading as="h1" size="lg">
          {scheduledExportId ? "Edit scheduled export" : "Export forms"}
        </Heading>
        {scheduledExportId && (
          <Popover placement="bottom-end" trigger="hover">
            <PopoverTrigger>
              <IconButton
                size="xl"
                icon={<RiQuestionLine />}
                variant="ghost"
                aria-label="Export filters"
                color="black"
              />
            </PopoverTrigger>
            <PopoverContent>
              <Box margin={6}>
                <Tag size="sm" variant="solid" colorScheme="purple" mb={2}>
                  <Icon boxSize={4} as={RiLightbulbLine} paddingRight={1} />
                  <Text>Tip</Text>
                </Tag>
                <Text fontSize="sm">
                  Filters for a scheduled export cannot be edited, but you can
                  view the filters for this scheduled export by going to the{" "}
                  <Link
                    href={organization
                      .map((org) =>
                        Url.OrgAdmin.Forms.index({
                          organization: org,
                          formTemplateId: selectedNavFormTemplate?.id ?? "",
                          params:
                            scheduledExportData?.exportConfig
                              .searchParamsString,
                        })
                      )
                      .withDefault("#")}
                    target="_blank"
                    color="primary.500"
                    textDecoration="underline"
                  >
                    form list
                  </Link>
                  .
                </Text>
              </Box>
            </PopoverContent>
          </Popover>
        )}
      </HStack>

      {isLoading && <Loading />}

      {hasError && !isLoading && <GenericError />}

      {!isLoading &&
        !hasError &&
        selectedNavFormTemplate &&
        selection &&
        bulkExportProps.data && (
          <BulkExportForm
            formTemplateId={selectedNavFormTemplate.id}
            selection={selection}
            dropoffFormTemplateId={dropoffFormTemplateId}
            {...bulkExportProps.data}
            scheduledExport={scheduledExportData}
          />
        )}
    </Flex>
  );
}
